// Erro da API, no formato RFC 9457 (application/problem+json).
//
// O contrato inteiro devolve erro assim, então a interface tem um só jeito de
// tratar falha — em vez de cada tela adivinhar se o corpo é {erro}, {message}
// ou texto solto.

/** Campos do problem+json que o contrato define. */
export interface Problema {
  type: string;
  title: string;
  status: number;
  detail?: string;
  /** Erros por campo, no formato { email: ["obrigatório"] }. */
  erros?: Record<string, string[]>;
}

export class ErroDaAPI extends Error {
  readonly problema: Problema;
  readonly status: number;

  constructor(problema: Problema) {
    super(problema.detail || problema.title);
    this.name = "ErroDaAPI";
    this.problema = problema;
    this.status = problema.status;
  }

  /** Mensagem para mostrar ao usuário, sem jargão de HTTP. */
  get mensagem(): string {
    return this.problema.detail || this.problema.title;
  }

  /** Erros do formulário, para marcar campo a campo. */
  get porCampo(): Record<string, string[]> {
    return this.problema.erros ?? {};
  }
}

/** Falha de rede: o servidor não respondeu. Diferente de ele ter recusado. */
export class ErroDeRede extends Error {
  constructor(causa: unknown) {
    super("Não foi possível falar com o servidor.");
    this.name = "ErroDeRede";
    this.cause = causa;
  }
}

/**
 * Traduz o erro em uma frase que o usuário entende.
 *
 * Os status que aparecem na tela do webmail merecem texto próprio; o resto cai
 * no `detail` que a API mandou, que já é escrito em português.
 */
export function explicar(erro: unknown): string {
  if (erro instanceof ErroDeRede) {
    return "Sem conexão com o servidor. Verifique a internet e tente de novo.";
  }
  if (!(erro instanceof ErroDaAPI)) {
    return "Algo deu errado.";
  }
  switch (erro.status) {
    case 401:
      return "Sua sessão expirou. Entre de novo.";
    case 403:
      return "Você não tem permissão para isso.";
    case 404:
      // 404 é também a resposta para recurso de outro cliente — de propósito,
      // para não confirmar que ele existe. Ver a fronteira entre clientes.
      return "Não encontrado.";
    case 413:
      return "Arquivo grande demais.";
    case 429:
      return "Muitas tentativas. Espere um pouco.";
    case 502:
    case 503:
    case 504:
      // O SERVIDOR NÃO RESPONDEU — e isso não é culpa de quem digitou.
      //
      // Sem este caso, um 502 caía no `default` e mostrava o título cru do
      // nginx. Na tela de login isso apareceu como "Erro" sozinho, e a
      // leitura natural é "minha senha está errada" — levando a pessoa a
      // tentar de novo, trocar a senha, duvidar de si. A causa era o
      // servidor fora do ar.
      return (
        "O servidor não está respondendo agora. Isso não é a sua senha — " +
        "é a plataforma. Tente de novo em instantes; se persistir, avise o suporte."
      );
    default:
      return erro.mensagem;
  }
}
